<template>
  <div class="view-inner">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-header-title"><i class="bi bi-file-earmark-text"></i> Gestión de Documentos</h1>
      <div class="page-header-actions">
        <button v-if="esSuperAdmin" class="btn-add" @click="abrirModal">
          <i class="bi bi-plus-lg"></i> Agregar Documento
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filter-card">
      <div class="row g-2">
        <div class="col-12 col-md-4">
          <label class="form-label">Búsqueda general</label>
          <input v-model="filtroBusqueda" type="text" class="form-control form-control-sm"
            placeholder="Nombre, placa, documento, observaciones..." />
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label">Tipo</label>
          <select v-model="filtroTipo" class="form-select form-select-sm">
            <option value="">Todos</option>
            <option v-for="t in opcionesTipo" :key="t" :value="t">{{ t }}</option>
          </select>
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label">Tipo de Vehículo</label>
          <select v-model="filtroTipoVehiculo" class="form-select form-select-sm">
            <option value="">Todos</option>
            <option v-for="t in opcionesTipoVehiculo" :key="t" :value="t">{{ t }}</option>
          </select>
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label">Documento</label>
          <select v-model="filtroDocumento" class="form-select form-select-sm">
            <option value="">Todos</option>
            <option v-for="d in opcionesDocumento" :key="d" :value="d">{{ d }}</option>
          </select>
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label">Estado</label>
          <select v-model="filtroEstado" class="form-select form-select-sm">
            <option value="">Todos</option>
            <option value="vencido">Vencidos</option>
            <option value="pronto">Por vencer (≤ 21 días)</option>
            <option value="vigente">Vigentes</option>
            <option value="sin-fecha">Sin fecha</option>
          </select>
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label">Vence desde</label>
          <input v-model="filtroFechaDesde" type="date" class="form-control form-control-sm" />
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label">Vence hasta</label>
          <input v-model="filtroFechaHasta" type="date" class="form-control form-control-sm" />
        </div>
        <div class="col-12 col-md-8 d-flex align-items-end justify-content-between flex-wrap gap-2">
          <div class="d-flex gap-2 flex-wrap">
            <button class="btn btn-sm btn-outline-danger" @click="filtroEstado = 'vencido'">
              <i class="bi bi-exclamation-octagon me-1"></i>Vencidos ({{ conteoEstados.vencido }})
            </button>
            <button class="btn btn-sm btn-outline-warning" @click="filtroEstado = 'pronto'">
              <i class="bi bi-clock-history me-1"></i>Por vencer ({{ conteoEstados.pronto }})
            </button>
          </div>
          <!-- Los filtros se aplican al escribir: no hay botón Buscar, solo Limpiar -->
          <div class="filter-actions">
            <button class="btn-clear" @click="limpiarFiltros"><i class="bi bi-x-circle"></i> Limpiar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla (estándar: ESTANDAR_LISTADOS.md) -->
    <div>
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th class="th-sortable" @click="ordenarPor('nombre')">
                Nombre/Placa <i :class="iconoOrden('nombre')"></i>
              </th>
              <th class="th-sortable" @click="ordenarPor('tipo')">
                Tipo <i :class="iconoOrden('tipo')"></i>
              </th>
              <th class="th-sortable" @click="ordenarPor('tipo_vehiculo')">
                Tipo de Vehículo <i :class="iconoOrden('tipo_vehiculo')"></i>
              </th>
              <th class="th-sortable" @click="ordenarPor('documento')">
                Documento <i :class="iconoOrden('documento')"></i>
              </th>
              <th class="th-sortable" @click="ordenarPor('fecha')">
                Fecha vencimiento <i :class="iconoOrden('fecha')"></i>
              </th>
              <th>Observaciones</th>
              <th class="col-acciones">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="doc in paginar(documentosFiltrados)" :key="doc.id">
              <td class="celda-entidad"><div class="entidad-nombre" :title="doc.nombre_placa || doc.nombre">{{ doc.nombre_placa || doc.nombre }}</div></td>
              <td>{{ doc.tipo }}</td>
              <td>{{ doc.tipo_vehiculo || '-' }}</td>
              <td>{{ doc.documento_tipo || doc.documentoTipo }}</td>
              <td class="celda-id">
                <span :class="obtenerClaseBadgeTonal(doc.fecha_vencimiento || doc.fechaVencimiento)">
                  {{ formatearFecha(doc.fecha_vencimiento || doc.fechaVencimiento) }}
                </span>
              </td>
              <td class="celda-entidad"><div class="entidad-nombre" :title="doc.observaciones">{{ doc.observaciones }}</div></td>
              <td class="col-acciones">
                <button class="btn-icon btn-icon-view" @click="verArchivo(doc)" title="Ver archivo" aria-label="Ver archivo"><i class="bi bi-eye"></i></button>
                <button v-if="esSuperAdmin" class="btn-icon btn-icon-edit" @click="abrirModalEditar(doc.id)" title="Editar" aria-label="Editar"><i class="bi bi-pencil"></i></button>
                <button v-if="esSuperAdmin" class="btn-icon btn-icon-del" @click="eliminarDocumento(doc.id)" title="Eliminar" aria-label="Eliminar"><i class="bi bi-trash"></i></button>
              </td>
            </tr>
            <tr v-if="!documentosFiltrados.length" class="empty-row">
              <td colspan="7">No se encontraron documentos</td>
            </tr>
          </tbody>
        </table>
      </div>
      <PaginacionListado :pagina="paginaActualDe(documentosFiltrados)" :total-paginas="totalPaginasDe(documentosFiltrados)"
        :total="documentosFiltrados.length" :por-pagina="porPagina" :en-pagina="paginar(documentosFiltrados).length"
        etiqueta="documentos" @cambiar="pagina = $event" />
    </div>

    <!-- Modal personalizado -->
    <div v-if="modalVisible" class="modal-backdrop"
      style="display:flex;align-items:center;justify-content:center;position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:1050;">
      <div class="modal-content p-3 bg-white rounded" style="max-width:500px; width: 90%;">
        <h5>Agregar Documento</h5>
        <form @submit.prevent="guardarDocumento">
          <div class="form-group">
            <label>Tipo</label>
            <select v-model="nuevoDoc.tipo" class="form-control" required @change="onTipoChange">
              <option value="" disabled>Seleccione tipo</option>
              <option value="Chofer">Chofer</option>
              <option value="Auxiliar">Auxiliar</option>
              <option value="Vehículo">Vehículo</option>
            </select>
          </div>
          <div class="form-group mt-2">
            <label>Nombre/Placa</label>
            <input v-model="nuevoDoc.nombre" type="text" class="form-control" required />
          </div>
          <div class="form-group mt-2" v-if="nuevoDoc.tipo === 'Vehículo'">
            <label>Tipo de Vehículo</label>
            <select v-model="nuevoDoc.tipoVehiculo" class="form-control" required>
              <option v-for="item in tipoUnidades" :key="item.id" :value="item.tipo_unidad">{{ item.tipo_unidad }}
              </option>
            </select>
          </div>

          <div class="form-group mt-2" v-if="documentosPosibles.length">
            <label>Documento</label>
            <div class="d-flex">
              <select v-model="nuevoDoc.documentoTipo" class="form-control" required>
                <option value="" disabled>Seleccione documento</option>
                <option v-for="docTipo in documentosPosibles" :key="docTipo" :value="docTipo">
                  {{ docTipo }}
                </option>
              </select>
              <button type="button" class="btn btn-outline-secondary ms-2" @click="abrirModalTipos"
                title="Gestionar tipos de documentos">
                <i class="bi bi-gear"></i>
              </button>
            </div>
          </div>

          <div class="form-group mt-2">
            <label>Archivo</label>
            <div class="custom-file">
              <input type="file" ref="fileInput" @change="onFileChange" class="custom-file-input" required />
              <label class="custom-file-label" style="overflow:hidden; text-overflow: ellipsis; white-space: nowrap;">
                {{ nuevoDoc.archivoNombre || "Seleccionar archivo..." }}
              </label>
            </div>
          </div>

          <div class="form-group mt-2">
            <label>Fecha de vencimiento</label>
            <input v-model="nuevoDoc.fechaVencimiento" type="date" class="form-control" required />
          </div>

          <div class="form-group mt-2">
            <label>Observaciones</label>
            <textarea v-model="nuevoDoc.observaciones" rows="2" class="form-control"
              placeholder="Ingrese observaciones (opcional)"></textarea>
          </div>

          <div class="text-right mt-3">
            <button type="submit" class="btn btn-primary float-end mx-2"
              :disabled="!nuevoDoc.archivo || !nuevoDoc.documentoTipo">
              Guardar
            </button>
            <button type="button" class="btn btn-secondary mr-2 float-end" @click="cerrarModal">Cancelar</button>

          </div>
        </form>
      </div>
    </div>

    <!-- Modal para gestionar tipos de documentos -->
    <div v-if="modalTiposVisible" class="modal-backdrop"
      style="display:flex;align-items:center;justify-content:center;position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:1055;">
      <div class="modal-content p-3 bg-white rounded"
        style="max-width:700px; width: 90%; max-height: 80vh; overflow-y: auto;">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5>Gestionar Tipos de Documentos</h5>
          <button type="button" class="btn-close" @click="cerrarModalTipos" aria-label="Close">×</button>
        </div>

        <!-- Formulario para agregar nuevo tipo -->
        <form @submit.prevent="agregarTipoDocumento" class="mb-4">
          <div class="row">
            <div class="col-md-4">
              <label>Tipo</label>
              <select v-model="nuevoTipo.tipo" class="form-control" required>
                <option value="" disabled>Seleccione tipo</option>
                <option value="Chofer">Chofer</option>
                <option value="Auxiliar">Auxiliar</option>
                <option value="Vehículo">Vehículo</option>
              </select>
            </div>
            <div class="col-md-6">
              <label>Documento</label>
              <input v-model="nuevoTipo.documento" type="text" class="form-control" required
                placeholder="Ej: DNI, Brevete, etc." />
            </div>
            <div class="col-md-2 d-flex align-items-end">
              <button type="submit" class="btn btn-primary">Agregar</button>
            </div>
          </div>
        </form>

        <!-- Lista de tipos existentes -->
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Documento</th>
                <th class="col-acciones">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in todosLosTipos" :key="item.id">
                <td>{{ item.tipo }}</td>
                <td class="celda-entidad"><div class="entidad-nombre" :title="item.documento">{{ item.documento }}</div></td>
                <td class="col-acciones">
                  <button class="btn-icon btn-icon-del" @click="eliminarTipoDocumento(item.id)" title="Eliminar" aria-label="Eliminar">
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="!todosLosTipos.length" class="empty-row">
                <td colspan="3">No se encontraron tipos de documentos</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="text-end">
          <button type="button" class="btn btn-secondary" @click="cerrarModalTipos">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Modal editar documento -->
    <div v-if="modalEditVisible" class="modal-backdrop"
      style="display:flex;align-items:center;justify-content:center;position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:1060;">
      <div class="modal-content p-3 bg-white rounded" style="max-width:500px; width: 90%;">
        <h5>Editar Documento</h5>
        <form @submit.prevent="guardarEdicionDocumento">
          <div class="form-group">
            <label>Tipo</label>
            <select v-model="editDoc.tipo" class="form-control" required @change="() => {
              editDoc.documentoTipo = '';
              editDoc.tipoVehiculo = '';
            }">
              <option value="" disabled>Seleccione tipo</option>
              <option value="Chofer">Chofer</option>
              <option value="Auxiliar">Auxiliar</option>
              <option value="Vehículo">Vehículo</option>
            </select>
          </div>
          <div class="form-group mt-2">
            <label>Nombre/Placa</label>
            <input v-model="editDoc.nombre" type="text" class="form-control" required />
          </div>
          <div class="form-group mt-2" v-if="editDoc.tipo === 'Vehículo'">
            <label>Tipo de Vehículo</label>{{ editDoc.tipoVehiculo }}
            <select v-model="editDoc.tipoVehiculo" class="form-control" required>
              <option v-for="item in tipoUnidades" :key="item.id" :value="item.tipo_unidad">{{ item.tipo_unidad }}
              </option>
            </select>
          </div>
          <div class="form-group mt-2" v-if="documentosPorTipo[editDoc.tipo]?.length">
            <label>Documento</label>
            <select v-model="editDoc.documentoTipo" class="form-control" required>
              <option value="" disabled>Seleccione documento</option>
              <option v-for="docTipo in documentosPorTipo[editDoc.tipo]" :key="docTipo" :value="docTipo">
                {{ docTipo }}
              </option>
            </select>
          </div>
          <div class="form-group mt-2">
            <label>Archivo</label>
            <div class="custom-file">
              <input type="file" @change="e => { if (e.target.files[0]) { editDoc.archivo = e.target.files[0]; } }"
                class="custom-file-input" />
              <label class="custom-file-label" style="overflow:hidden; text-overflow: ellipsis; white-space: nowrap;">
                {{ editDoc.archivo?.name || editDoc.archivoNombre || "Seleccionar archivo..." }}
              </label>
            </div>
          </div>
          <div class="form-group mt-2">
            <label>Fecha de vencimiento</label>
            <input v-model="editDoc.fechaVencimiento" type="date" class="form-control" required />
          </div>
          <div class="form-group mt-2">
            <label>Observaciones</label>
            <textarea v-model="editDoc.observaciones" rows="2" class="form-control"
              placeholder="Ingrese observaciones (opcional)"></textarea>
          </div>
          <div class="text-right mt-3">
            <button type="submit" class="btn btn-primary float-end mx-2">Guardar cambios</button>
            <button type="button" class="btn btn-secondary mr-2 float-end" @click="cerrarModalEditar">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import PaginacionListado from '@/components/PaginacionListado.vue'
import paginacionLocal from '@/mixins/paginacionLocal'

export default {
  components: { PaginacionListado },
  mixins: [paginacionLocal],
  data() {
    return {
      documentos: [],
      filtroBusqueda: "",
      filtroTipo: "",
      filtroTipoVehiculo: "",
      filtroDocumento: "",
      filtroEstado: "",
      filtroFechaDesde: "",
      filtroFechaHasta: "",
      ordenCampo: "fecha",
      ordenAsc: true,
      modalVisible: false,
      modalEditVisible: false,
      nuevoDoc: {
        nombre: "",
        tipo: "",
        documentoTipo: "",
        archivo: null,
        archivoNombre: "",
        url: "",
        observaciones: "",
        fechaVencimiento: "",
        tipoVehiculo: ""
      },
      editDoc: null,
      documentosPorTipo: {},
      documentosPosibles: [],
      tipoUnidades: [],
      modalTiposVisible: false,
      todosLosTipos: [],
      nuevoTipo: {
        tipo: "",
        documento: ""
      },
      esSuperAdmin: false
    };
  },
  mounted() {
    this.getTipoUnidades();
    this.cargarDocumentosPorTipo();
    this.listarDocumentos();
    // Detectar si es superadmin (ajusta según tu lógica de autenticación)
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    this.esSuperAdmin = user.cargo === 'admin';

  },
  watch: {
    // Los filtros se aplican al escribir: cualquier cambio de filtro u orden vuelve a la página 1
    claveFiltros() {
      this.pagina = 1;
    },
  },
  computed: {
    opcionesTipo() {
      return this.valoresUnicos((d) => d.tipo);
    },
    opcionesTipoVehiculo() {
      return this.valoresUnicos((d) => d.tipo_vehiculo);
    },
    opcionesDocumento() {
      return this.valoresUnicos((d) => d.documento_tipo || d.documentoTipo);
    },
    conteoEstados() {
      const conteo = { vencido: 0, pronto: 0, vigente: 0, 'sin-fecha': 0 };
      this.documentos.forEach((d) => {
        const estado = this.estadoVencimiento(d.fecha_vencimiento || d.fechaVencimiento);
        conteo[estado] += 1;
      });
      return conteo;
    },
    documentosFiltrados() {
      const busqueda = (this.filtroBusqueda || '').trim().toLowerCase();

      const filtrados = this.documentos.filter((doc) => {
        const nombre = doc.nombre_placa || doc.nombre || '';
        const tipo = doc.tipo || '';
        const tipoVehiculo = doc.tipo_vehiculo || '';
        const documento = doc.documento_tipo || doc.documentoTipo || '';
        const observaciones = doc.observaciones || '';
        const fecha = doc.fecha_vencimiento || doc.fechaVencimiento;

        if (busqueda) {
          const texto = `${nombre} ${tipo} ${tipoVehiculo} ${documento} ${observaciones}`.toLowerCase();
          if (!texto.includes(busqueda)) return false;
        }

        if (this.filtroTipo && tipo !== this.filtroTipo) return false;
        if (this.filtroTipoVehiculo && tipoVehiculo !== this.filtroTipoVehiculo) return false;
        if (this.filtroDocumento && documento !== this.filtroDocumento) return false;
        if (this.filtroEstado && this.estadoVencimiento(fecha) !== this.filtroEstado) return false;

        // Los rangos de fecha solo aplican a documentos que tienen vencimiento.
        if (this.filtroFechaDesde || this.filtroFechaHasta) {
          if (!fecha) return false;
          const soloFecha = String(fecha).slice(0, 10);
          if (this.filtroFechaDesde && soloFecha < this.filtroFechaDesde) return false;
          if (this.filtroFechaHasta && soloFecha > this.filtroFechaHasta) return false;
        }

        return true;
      });

      return this.ordenarDocumentos(filtrados);
    },
    // Cambia solo cuando cambian los filtros o el orden (no al recargar los datos)
    claveFiltros() {
      return JSON.stringify([
        this.filtroBusqueda, this.filtroTipo, this.filtroTipoVehiculo, this.filtroDocumento,
        this.filtroEstado, this.filtroFechaDesde, this.filtroFechaHasta, this.ordenCampo, this.ordenAsc,
      ]);
    },
  },
  methods: {
    cerrarModalTipos() {
      this.modalTiposVisible = false;
      this.resetNuevoTipo();
      // Refresca documentosPosibles si hay tipo seleccionado
      if (this.nuevoDoc && this.nuevoDoc.tipo) {
        this.onTipoChange();
      }
    },
    async eliminarTipoDocumento(id) {
      const result = await Swal.fire({
        title: '¿Está seguro?',
        text: '¿Desea eliminar este tipo de documento?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      });
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`documentos-por-tipo/${id}`);
        Swal.fire({
          icon: 'success',
          title: '¡Eliminado!',
          text: 'Tipo de documento eliminado correctamente',
          timer: 2000,
          showConfirmButton: false
        });
        await this.cargarDocumentosPorTipo();
        // Refresca documentosPosibles si hay tipo seleccionado
        if (this.nuevoDoc && this.nuevoDoc.tipo) {
          this.onTipoChange();
        }
      } catch (error) {
        console.error('Error al eliminar tipo de documento:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo eliminar el tipo de documento: ' + (error.response?.data?.message || error.message)
        });
      }
    },
    obtenerClaseBadge(fechaStr) {
      if (!fechaStr) return 'badge bg-secondary';
      const hoy = new Date();
      const fecha = new Date(fechaStr);
      const diferenciaDias = Math.ceil((fecha - hoy) / (1000 * 60 * 60 * 24));
      if (diferenciaDias < 0) return 'badge bg-danger';
      if (diferenciaDias <= 21) return 'badge bg-warning text-dark';
      return 'badge bg-success';
    },
    estadoVencimiento(fechaStr) {
      if (!fechaStr) return 'sin-fecha';
      const hoy = new Date();
      const fecha = new Date(fechaStr);
      if (isNaN(fecha)) return 'sin-fecha';
      const diferenciaDias = Math.ceil((fecha - hoy) / (1000 * 60 * 60 * 24));
      if (diferenciaDias < 0) return 'vencido';
      if (diferenciaDias <= 21) return 'pronto';
      return 'vigente';
    },
    obtenerClaseBadgeTonal(fechaStr) {
      const estado = this.estadoVencimiento(fechaStr);
      if (estado === 'sin-fecha') return 'badge-doc badge-doc-neutral';
      return `badge-doc badge-doc-${estado}`;
    },
    valoresUnicos(selector) {
      const valores = this.documentos
        .map(selector)
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
      return [...new Set(valores)].sort((a, b) => String(a).localeCompare(String(b), 'es'));
    },
    limpiarFiltros() {
      this.filtroBusqueda = '';
      this.filtroTipo = '';
      this.filtroTipoVehiculo = '';
      this.filtroDocumento = '';
      this.filtroEstado = '';
      this.filtroFechaDesde = '';
      this.filtroFechaHasta = '';
      this.pagina = 1;
    },
    ordenarPor(campo) {
      if (this.ordenCampo === campo) {
        this.ordenAsc = !this.ordenAsc;
      } else {
        this.ordenCampo = campo;
        this.ordenAsc = true;
      }
    },
    iconoOrden(campo) {
      if (this.ordenCampo !== campo) return 'bi bi-arrow-down-up text-muted small';
      return this.ordenAsc ? 'bi bi-sort-down-alt' : 'bi bi-sort-up-alt';
    },
    valorParaOrden(doc, campo) {
      switch (campo) {
        case 'nombre':
          return (doc.nombre_placa || doc.nombre || '').toLowerCase();
        case 'tipo':
          return (doc.tipo || '').toLowerCase();
        case 'tipo_vehiculo':
          return (doc.tipo_vehiculo || '').toLowerCase();
        case 'documento':
          return (doc.documento_tipo || doc.documentoTipo || '').toLowerCase();
        case 'fecha': {
          const fecha = doc.fecha_vencimiento || doc.fechaVencimiento;
          // Los documentos sin fecha van siempre al final.
          return fecha ? String(fecha).slice(0, 10) : '9999-12-31';
        }
        default:
          return '';
      }
    },
    ordenarDocumentos(lista) {
      const campo = this.ordenCampo;
      const factor = this.ordenAsc ? 1 : -1;

      return [...lista].sort((a, b) => {
        const va = this.valorParaOrden(a, campo);
        const vb = this.valorParaOrden(b, campo);
        if (va === vb) return 0;
        return va > vb ? factor : -factor;
      });
    },
    async getTipoUnidades() {
      try {
        let ids = [1, 2, 3, 4, 5, 6, 7];
        const params = ids.length ? { ids: ids.join(',') } : {} // Convertir array a string si hay IDs
        const response = await axios.get('tipo-unidades', { params })
        this.tipoUnidades = response.data
        console.log('this.tipoUnidades:', this.tipoUnidades)
      } catch (error) {
        console.error('Error cargando tipoUnidades:', error)
      }
    },
    async cargarDocumentosPorTipo() {
      try {
        const response = await axios.get('documentos-por-tipo')
        this.todosLosTipos = response.data.data

        // Organizar los datos por tipo para el objeto documentosPorTipo
        this.documentosPorTipo = {}
        this.todosLosTipos.forEach(item => {
          if (!this.documentosPorTipo[item.tipo]) {
            this.documentosPorTipo[item.tipo] = []
          }
          this.documentosPorTipo[item.tipo].push(item.documento)
        })

        console.log('Documentos por tipo cargados:', this.documentosPorTipo)
        console.log('Todos los tipos:', this.todosLosTipos)
      } catch (error) {
        console.error('Error cargando documentos por tipo:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error al cargar los tipos de documentos desde el servidor'
        })
      }
    },
    async listarDocumentos() {
      try {
        const response = await axios.get('documentos-personal-vehiculo');
        this.documentos = response.data || [];
        console.log("this.documentos", this.documentos)
      } catch (error) {
        console.error('Error al listar documentos:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los documentos.'
        });
      }
    },
    formatearFecha(fechaStr) {
      if (!fechaStr) return '';
      const date = new Date(fechaStr);
      return date.toLocaleDateString();
    },
    abrirModal() {
      this.resetNuevoDoc();
      this.modalVisible = true;
    },
    cerrarModal() {
      this.modalVisible = false;
    },
    onTipoChange() {
      // Actualiza los documentos posibles según el tipo seleccionado
      this.documentosPosibles = this.nuevoDoc.tipo
        ? this.documentosPorTipo[this.nuevoDoc.tipo]
        : [];
      this.nuevoDoc.documentoTipo = ""; // reset documento seleccionado
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.nuevoDoc.archivo = file;
        this.nuevoDoc.archivoNombre = file.name;
      } else {
        this.nuevoDoc.archivo = null;
        this.nuevoDoc.archivoNombre = "";
      }
    },
    async guardarDocumento() {
      if (
        !this.nuevoDoc.nombre ||
        !this.nuevoDoc.tipo ||
        !this.nuevoDoc.archivo ||
        !this.nuevoDoc.documentoTipo ||
        !this.nuevoDoc.fechaVencimiento
      ) {
        Swal.fire({
          icon: 'warning',
          title: 'Campos requeridos',
          text: 'Por favor complete todos los campos obligatorios'
        });
        return;
      }
      try {
        const formData = new FormData();
        formData.append('nombre_placa', this.nuevoDoc.nombre); // nombre_placa
        formData.append('tipo', this.nuevoDoc.tipo);
        formData.append('documento_tipo', this.nuevoDoc.documentoTipo); // documento_tipo
        formData.append('archivo', this.nuevoDoc.archivo);
        formData.append('archivo_nombre', this.nuevoDoc.archivoNombre); // archivo_nombre
        formData.append('fecha_vencimiento', this.nuevoDoc.fechaVencimiento); // fecha_vencimiento
        formData.append('observaciones', this.nuevoDoc.observaciones);
        formData.append('tipo_vehiculo', this.nuevoDoc.tipoVehiculo); // tipo_vehiculo

        const response = await axios.post('documentos-personal-vehiculo', formData);
        Swal.fire({
          icon: 'success',
          title: '¡Éxito!',
          text: response.data.message || 'Documento guardado correctamente',
          timer: 2000,
          showConfirmButton: false
        });
        this.cerrarModal();
        this.listarDocumentos();
      } catch (error) {
        console.error('Error al guardar documento:', error);
        let msg = 'No se pudo guardar el documento.';
        if (error.response?.data?.errors) {
          msg += '\n' + Object.values(error.response.data.errors).map(arr => arr.join(', ')).join('\n');
        }
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: msg
        });
      }
    },
    async eliminarDocumento(id) {
      const result = await Swal.fire({
        title: '¿Está seguro?',
        text: '¿Desea eliminar este documento?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      });
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`documentos-personal-vehiculo/${id}`);
        Swal.fire({
          icon: 'success',
          title: '¡Eliminado!',
          text: 'Documento eliminado correctamente',
          timer: 2000,
          showConfirmButton: false
        });
        this.listarDocumentos();
      } catch (error) {
        console.error('Error al eliminar documento:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo eliminar el documento: ' + (error.response?.data?.message || error.message)
        });
      }
    },
    resetNuevoDoc() {
      this.nuevoDoc = {
        nombre: '',
        tipo: '',
        documentoTipo: '',
        archivo: null,
        archivoNombre: '',
        url: '',
        observaciones: '',
        fechaVencimiento: '',
        tipoVehiculo: ''
      };
      this.documentosPosibles = [];
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    abrirModalTipos() {
      this.modalTiposVisible = true;
      this.resetNuevoTipo();
    },

    resetNuevoTipo() {
      // Siempre inicializa como objeto válido
      this.nuevoTipo = { tipo: '', documento: '' };
    },
    // Editar documento
    async abrirModalEditar(docId) {
      try {
        const response = await axios.get(`documentos-personal-vehiculo/${docId}`);
        const doc = response.data.data || response.data || {};
        // Normalizar campos para el modal de edición
        this.editDoc = {
          id: doc.id,
          nombre: doc.nombre_placa || '',
          tipo: doc.tipo || '',
          documentoTipo: doc.documento_tipo || '',
          archivo: null,
          archivoNombre: doc.archivo_nombre || '',
          url: doc.archivo_url ? ('/' + doc.archivo_url) : '',
          observaciones: doc.observaciones || '',
          fechaVencimiento: doc.fecha_vencimiento ? doc.fecha_vencimiento.substring(0, 10) : '',
          tipoVehiculo: doc.tipo_vehiculo || ''
        };
        this.modalEditVisible = true;
      } catch (error) {
        console.error('Error al cargar documento para editar:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar el documento para editar.'
        });
      }
    },
    cerrarModalEditar() {
      this.modalEditVisible = false;
      this.editDoc = null;
    },
    async guardarEdicionDocumento() {
      if (!this.editDoc) return;
      try {
        const formData = new FormData();
        formData.append('nombre_placa', this.editDoc.nombre); // nombre_placa
        formData.append('tipo', this.editDoc.tipo);
        formData.append('documento_tipo', this.editDoc.documentoTipo); // documento_tipo
        if (this.editDoc.archivo instanceof File) {
          formData.append('archivo', this.editDoc.archivo);
          formData.append('archivo_nombre', this.editDoc.archivo.name); // archivo_nombre
        }
        formData.append('fecha_vencimiento', this.editDoc.fechaVencimiento); // fecha_vencimiento
        formData.append('observaciones', this.editDoc.observaciones);
        formData.append('tipo_vehiculo', this.editDoc.tipoVehiculo); // tipo_vehiculo

        const response = await axios.post(`documentos-personal-vehiculo/${this.editDoc.id}`, formData);
        Swal.fire({
          icon: 'success',
          title: '¡Editado!',
          text: response.data.message || 'Documento editado correctamente',
          timer: 2000,
          showConfirmButton: false
        });
        this.cerrarModalEditar();
        this.listarDocumentos();
      } catch (error) {
        console.error('Error al editar documento:', error);
        let msg = 'No se pudo editar el documento.';
        if (error.response?.data?.errors) {
          msg += '\n' + Object.values(error.response.data.errors).map(arr => arr.join(', ')).join('\n');
        }
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: msg
        });
      }
    },
    verArchivo(archivo) {
      const nombre = archivo.archivo_url; // o archivo.archivo_url si guardas el nombre
      if (!nombre) {
        Swal.fire({
          icon: 'warning',
          title: 'Sin archivo',
          text: 'No hay archivo disponible para este documento.'
        });
        return;
      }

      // El archivo se pide con el token (axios) y se abre como blob en una pestaña nueva.
      // La pestaña se abre antes de la petición para que el navegador no la bloquee como popup.
      const ventana = window.open('', '_blank');
      axios.get('documento', { params: { ruta: nombre }, responseType: 'blob' })
        .then(({ data }) => {
          ventana.location.href = URL.createObjectURL(data);
        })
        .catch(() => {
          ventana.close();
          Swal.fire({
            icon: 'error',
            title: 'No se pudo abrir el archivo',
            text: 'El archivo no existe o no tienes permiso para verlo.'
          });
        });
    },

    // Método para agregar tipo de documento
    async agregarTipoDocumento() {
      if (!this.nuevoTipo.tipo || !this.nuevoTipo.documento) {
        Swal.fire({
          icon: 'warning',
          title: 'Campos requeridos',
          text: 'Por favor complete todos los campos para agregar un tipo de documento.'
        });
        return;
      }
      try {
        // Aquí deberías llamar a tu API para guardar el nuevo tipo
        const response = await axios.post('documentos-por-tipo', {
          tipo: this.nuevoTipo.tipo,
          documento: this.nuevoTipo.documento
        });
        Swal.fire({
          icon: 'success',
          title: '¡Tipo agregado!',
          text: response.data.message || 'Tipo de documento agregado correctamente',
          timer: 2000,
          showConfirmButton: false
        });
        this.resetNuevoTipo();
        this.cargarDocumentosPorTipo();
        this.onTipoChange();
      } catch (error) {
        console.error('Error al agregar tipo de documento:', error);
        let msg = 'No se pudo agregar el tipo de documento.';
        if (error.response?.data?.errors) {
          msg += '\n' + Object.values(error.response.data.errors).map(arr => arr.join(', ')).join('\n');
        }
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: msg
        });
      }
    }


  },
};
</script>

<style scoped>
.custom-file-input {
  cursor: pointer;
}

.custom-file-label {
  cursor: pointer;
}

.badge {
  font-size: 0.75rem
}

/* Tonal date badges */
.badge-doc {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}
.badge-doc-vigente  { background: #e8f5e9; color: #2e7d32; }
.badge-doc-pronto   { background: #fff3e0; color: #e65100; }
.badge-doc-vencido  { background: #ffebee; color: #c62828; }
.badge-doc-neutral  { background: #f5f5f5; color: #757575; }

.th-sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
</style>
